import { Controller } from 'stimulus'

// CFM Performance Test entry panel (specialized cfm_test tasks).
// Live-computes row averages / CFM / totals and the Velocity Distribution
// charts from the sensor-ring areas the server renders, and autosaves the
// whole readings blob (debounced) to Jobs::CfmTestsController — the server
// recomputes via Cfm::Calculator and returns the refreshed results panel.
const SVGNS = 'http://www.w3.org/2000/svg'
const AXES = 4
const SERIES_VARS = ['--cfm-s1', '--cfm-s2', '--cfm-s3', '--cfm-s4']

export default class extends Controller {
  static targets = ['status', 'results', 'method2Empty', 'method2Section'];

  connect() {
    this.areas = JSON.parse(this.data.get('areas') || '[]')
    this.sensorCount = this.areas.length
    this.runBlocks().forEach(run => this.recomputeRun(run))
    this.refreshTotalsChips()
    this.restoreTab()
  }

  // ---- sub-tabs (Setup | Method 1 | Method 2 | Summary) ----

  showTab(event) {
    const tabKey = event.currentTarget.dataset.cfmTabRef
    this.activateTab(tabKey)
    try {
      sessionStorage.setItem(this.tabStorageKey(), tabKey)
    } catch (e) { /* private browsing */ }
  }

  activateTab(tabKey) {
    this.element.querySelectorAll('[data-cfm-tab]').forEach((panel) => {
      const node = panel
      node.style.display = node.dataset.cfmTab === tabKey ? '' : 'none'
    })
    this.element.querySelectorAll('[data-cfm-tab-ref]').forEach((button) => {
      const active = button.dataset.cfmTabRef === tabKey
      button.classList.toggle('active_tab', active)
      button.classList.toggle('inactive_tab', !active)
    })
  }

  // Restore the last-viewed tab (e.g. after the diameter-change reload);
  // otherwise the server's smart default stands.
  restoreTab() {
    let tabKey = null
    try {
      tabKey = sessionStorage.getItem(this.tabStorageKey())
    } catch (e) { /* private browsing */ }
    if (tabKey && this.element.querySelector(`[data-cfm-tab="${tabKey}"]`)) this.activateTab(tabKey)
  }

  tabStorageKey() {
    return `cfm-tab:${this.data.get('url')}`
  }

  // ---- entry events ----

  fieldChanged() { this.queueSave() }

  setupChanged() { this.queueSave() }

  gridChanged(event) {
    const run = event.target.closest('[data-cfm-run]')
    if (run) this.recomputeRun(run)
    this.refreshTotalsChips()
    this.queueSave()
  }

  // Paste a rows × columns block from Excel starting at the focused cell.
  pasteGrid(event) {
    const text = (event.clipboardData || window.clipboardData).getData('text')
    if (!text || !/[\t\n]/.test(text)) return // single value: default behavior

    event.preventDefault()
    const anchor = event.target
    const run = anchor.closest('[data-cfm-run]')
    const startRow = parseInt(anchor.dataset.cfmRow, 10)
    const startAxis = parseInt(anchor.dataset.cfmAxis, 10)
    text.trim().split(/\r?\n/).forEach((line, rowOffset) => {
      line.split('\t').forEach((value, axisOffset) => {
        const input = run.querySelector(
          `[data-cfm-row="${startRow + rowOffset}"][data-cfm-axis="${startAxis + axisOffset}"]`
        )
        if (input && !input.disabled) input.value = value.trim()
      })
    })
    this.recomputeRun(run)
    this.refreshTotalsChips()
    this.queueSave()
  }

  // ---- section toggles ----

  showSample(event) {
    const pill = event.currentTarget
    const method = pill.closest('[data-cfm-method]')
    const wanted = pill.dataset.cfmSampleRef
    method.querySelectorAll(':scope > [data-cfm-sample]').forEach((section) => {
      const node = section
      node.style.display = node.dataset.cfmSample === wanted ? '' : 'none'
    })
    // tab_class states, matching the app's segmented view toggles
    method.querySelectorAll('.cfm-sample-pill').forEach((button) => {
      const active = button.dataset.cfmSampleRef === wanted
      button.classList.toggle('text-white', active)
      button.classList.toggle('bg-cool-gray-400', active)
      button.classList.toggle('text-gray-500', !active)
      button.classList.toggle('bg-white', !active)
    })
  }

  toggleSpeed(event) {
    if (event.target.closest('input, button, select')) return
    const panel = event.currentTarget.closest('.cfm-speed')
    const body = panel.querySelector('.cfm-speed-body')
    const collapsed = body.style.display === 'none'
    body.style.display = collapsed ? '' : 'none'
    panel.querySelector('.cfm-caret').textContent = collapsed ? '▼' : '▶'
  }

  addMethod2() {
    this.method2EmptyTarget.style.display = 'none'
    this.method2SectionTarget.style.display = ''
  }

  // Clears every Mounting Method 2 reading (plus its downrod length) and
  // collapses the section back to the Add button.
  removeMethod2() {
    const section = this.method2SectionTarget
    const inputs = Array.from(section.querySelectorAll('input'))
    const hasData = inputs.some(input => input.value !== '')
    if (hasData && !window.confirm('Remove Mounting Method 2? All of its readings will be cleared.')) return

    inputs.forEach((input) => {
      const field = input
      field.value = ''
    })
    const downrod = this.element.querySelector('[data-cfm-setup="downrod_method_2_in"]')
    if (downrod) downrod.value = ''
    section.querySelectorAll('[data-cfm-run]').forEach(run => this.recomputeRun(run))
    this.refreshTotalsChips()
    section.style.display = 'none'
    this.method2EmptyTarget.style.display = ''
    this.queueSave()
  }

  // ---- live grid computation ----

  runBlocks() {
    return Array.from(this.element.querySelectorAll('[data-cfm-run]'))
  }

  runValues(run) {
    const rows = []
    for (let row = 0; row < this.sensorCount; row += 1) {
      const values = []
      for (let axis = 0; axis < AXES; axis += 1) {
        const input = run.querySelector(`[data-cfm-row="${row}"][data-cfm-axis="${axis}"]`)
        const value = input ? parseFloat(input.value) : NaN
        values.push(Number.isFinite(value) ? value : null)
      }
      rows.push(values)
    }
    return rows
  }

  recomputeRun(run) {
    const rows = this.runValues(run)
    const avgCells = run.querySelectorAll('[data-cfm-avg]')
    const cfmCells = run.querySelectorAll('[data-cfm-cfm]')
    let total = 0
    rows.forEach((values, index) => {
      const present = values.filter(v => v !== null)
      const avg = present.length ? present.reduce((a, b) => a + b, 0) / present.length : null
      const cfm = avg !== null && avg > 0 ? this.areas[index] * avg : 0
      total += cfm
      if (avgCells[index]) avgCells[index].textContent = avg === null ? '' : this.fmt(avg, 1)
      if (cfmCells[index]) cfmCells[index].textContent = avg === null ? '' : this.fmt(cfm, 1)
    })
    const block = run
    const totalCell = block.querySelector('[data-cfm-total]')
    if (totalCell) totalCell.textContent = total > 0 ? this.fmt(total, 1) : ''
    block.dataset.cfmRunTotal = total > 0 ? total.toFixed(1) : ''
    this.drawChart(block, rows)
  }

  refreshTotalsChips() {
    this.element.querySelectorAll('.cfm-speed').forEach((panel) => {
      const chip = panel.querySelector('[data-cfm-totals-chip]')
      if (!chip) return
      const totals = Array.from(panel.querySelectorAll('[data-cfm-run]'))
        .map(run => run.dataset.cfmRunTotal)
        .filter(t => t)
      chip.textContent = totals.length ? `${totals.map(t => this.fmt(parseFloat(t), 1)).join(' / ')} CFM` : ''
    })
  }

  // ---- autosave ----

  queueSave() {
    if (this.data.get('editable') !== 'true') return
    if (this.saveTimer) clearTimeout(this.saveTimer)
    this.setStatus('Unsaved changes…')
    this.saveTimer = setTimeout(() => this.save(), 800)
  }

  save() {
    this.setStatus('Saving…')
    const payload = { cfm_test: this.serializeSetup() }
    payload.cfm_test.data_json = JSON.stringify(this.serializeData())

    fetch(this.data.get('url'), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    })
      .then(response => response.json().then(json => ({ ok: response.ok, json })))
      .then(({ ok, json }) => {
        if (!ok) {
          this.setStatus((json.errors || ['Save failed']).join(', '))
          return
        }
        this.setStatus(`Saved ${new Date().toLocaleTimeString()}`)
        if (this.hasResultsTarget) this.resultsTarget.innerHTML = json.results_html
        this.refreshStabilityChips(json.results)
        // A diameter change resizes the sensor grid server-side — re-render.
        if (json.sensor_count !== this.sensorCount) window.location.reload()
      })
      .catch(() => this.setStatus('Save failed — check your connection'))
  }

  serializeSetup() {
    const setup = {}
    this.element.querySelectorAll('[data-cfm-setup]').forEach((input) => {
      setup[input.dataset.cfmSetup] = input.value
    })
    return setup
  }

  serializeData() {
    const data = { methods: {} }
    this.element.querySelectorAll('[data-cfm-method]').forEach((methodEl) => {
      const methodKey = methodEl.dataset.cfmMethod
      const samples = {}
      methodEl.querySelectorAll(':scope > [data-cfm-sample]').forEach((sampleEl) => {
        const speeds = {}
        sampleEl.querySelectorAll('[data-cfm-speed]').forEach((speedEl) => {
          const speed = {}
          speedEl.querySelectorAll('[data-cfm-scalar]').forEach((input) => {
            if (input.value !== '') speed[input.dataset.cfmScalar] = input.value
          })
          const runs = {}
          speedEl.querySelectorAll('[data-cfm-run]').forEach((runEl) => {
            const velocities = this.runValues(runEl)
            if (velocities.some(row => row.some(v => v !== null))) {
              runs[runEl.dataset.cfmRun] = { velocities }
            }
          })
          if (Object.keys(runs).length) speed.runs = runs
          if (Object.keys(speed).length) speeds[speedEl.dataset.cfmSpeed] = speed
        })
        if (Object.keys(speeds).length) samples[sampleEl.dataset.cfmSample] = speeds
      })
      if (Object.keys(samples).length) data.methods[methodKey] = { samples }
    })
    return data
  }

  refreshStabilityChips(results) {
    if (!results || !results.methods) return
    Object.keys(results.methods).forEach((methodKey) => {
      const methodResults = results.methods[methodKey]
      Object.keys(methodResults.samples || {}).forEach((sampleKey) => {
        ['high', 'low'].forEach((speed) => {
          const holder = this.element.querySelector(
            `[data-cfm-stability="${methodKey}-${sampleKey}-${speed}"]`
          )
          if (!holder) return
          const { stability } = methodResults.samples[sampleKey][speed] || {}
          if (!stability) { holder.innerHTML = ''; return }
          const good = stability.stable
          holder.innerHTML = `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${good ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}">${good ? 'Stable' : 'Unstable'} · max sensor Δ ${stability.max_delta_pct}% (limit ${Math.round(stability.limit_pct)}%)</span>`
        })
      })
    })
  }

  // ---- Velocity Distribution chart (one per run, axes A-D vs distance) ----

  drawChart(run, rows) {
    const host = run.querySelector('[data-cfm-chart]')
    if (!host) return
    const filled = rows
      .map((values, index) => ({ index, values }))
      .filter(row => row.values.some(v => v !== null))
    if (filled.length < 2) { host.innerHTML = ''; return }

    host.innerHTML = ''
    const W = 460; const H = 170
    const padL = 36; const padR = 20; const padT = 8; const padB = 20
    const positions = filled.map(row => row.index * 4)
    const maxD = Math.max(...positions)
    let maxV = 0
    filled.forEach(row => row.values.forEach((v) => { if (v !== null) maxV = Math.max(maxV, v) }))
    const yTop = Math.max(100, Math.ceil(maxV / 100) * 100)
    const x = d => padL + (d / maxD) * (W - padL - padR)
    const y = v => padT + (1 - v / yTop) * (H - padT - padB)

    const svg = this.svg('svg', { viewBox: `0 0 ${W} ${H}`, class: 'cfm-chart-svg', role: 'img', 'aria-label': 'Velocity distribution by axis' }, host)
    const head = document.createElement('div')
    head.className = 'cfm-chart-head'
    head.innerHTML = `<span>Velocity Distribution</span><span class="cfm-chart-units">FPM by distance from center (in)</span>${['A', 'B', 'C', 'D'].map((label, i) => `<span class="cfm-chart-key"><i style="background:var(${SERIES_VARS[i]})"></i>${label}</span>`).join('')}`
    host.insertBefore(head, svg)

    for (let v = 0; v <= yTop; v += yTop / 4) {
      this.svg('line', { x1: padL, x2: W - padR, y1: y(v), y2: y(v), class: 'cfm-chart-grid' }, svg)
      this.svg('text', { x: padL - 5, y: y(v) + 3, 'text-anchor': 'end', class: 'cfm-chart-axis' }, svg).textContent = v
    }
    positions.forEach((d) => {
      this.svg('text', { x: x(d), y: H - 6, 'text-anchor': 'middle', class: 'cfm-chart-axis' }, svg).textContent = `${d}"`
    })
    for (let axis = 0; axis < AXES; axis += 1) {
      const points = filled
        .filter(row => row.values[axis] !== null)
        .map(row => `${x(row.index * 4)},${y(row.values[axis])}`)
      if (points.length >= 2) {
        this.svg('polyline', { points: points.join(' '), fill: 'none', 'stroke-width': 2, 'stroke-linejoin': 'round', stroke: `var(${SERIES_VARS[axis]})` }, svg)
      }
    }
  }

  svg(tag, attrs, parent) {
    const el = document.createElementNS(SVGNS, tag)
    Object.keys(attrs).forEach(k => el.setAttribute(k, attrs[k]))
    if (parent) parent.appendChild(el)
    return el
  }

  fmt(value, precision) {
    return value.toLocaleString('en-US', { minimumFractionDigits: precision, maximumFractionDigits: precision })
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }
}
